import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { HiDocumentDownload, HiUserGroup } from "react-icons/hi";
import { SideDrawer } from "../../../atoms/SideDrawer";
import { ModalShell } from "../../../atoms/ModalShell";
import { CustomButton } from "../../../atoms/CustomButton";
import { Copy } from "../../../atoms/Copy";
import { NoTranslate } from "../../../atoms/NoTranslate";
import UserAvatar from "../../../atoms/UserAvatar";
import { Spinner } from "../../../atoms/Spinner";
import SendPersonalMessage from "../entities/SendPersonalMessage";
import PartnerLedgerSection from "./PartnerLedgerSection";
import { useRootSendContext } from "../entities/sendPersonalMessage/useRootSendContext";
import { SEND_MESSAGE_TYPE } from "../../../../constants/types";
import PartnerAdminService, {
  AttachWarning,
  PartnerClient,
  PartnerDetail,
} from "../../../../services/partnerAdmin";
import {
  contractBadge,
  formatDay,
  formatPayModDay,
  levelLabel,
  partnerName,
  payoutMethodLabel,
  programBadge,
  sponsoredText,
  subscriptionBadge,
  tierLabel,
  warningText,
} from "./partnerLabels";

const FIELD =
  "w-full rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm";

interface PartnerDrawerProps {
  partnerId: string | null;
  customerId: string;
  entityId: string;
  onClose: () => void;
  onChanged: () => void;
}

const errorMessage = (error: any) =>
  error?.response?.data?.message || error?.message || "Request failed";

const PartnerDrawer: React.FC<PartnerDrawerProps> = ({
  partnerId,
  customerId,
  entityId,
  onClose,
  onChanged,
}) => {
  const [partner, setPartner] = useState<PartnerDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [attachId, setAttachId] = useState("");
  const [attachReason, setAttachReason] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [warnings, setWarnings] = useState<AttachWarning[] | null>(null);
  const [detachTarget, setDetachTarget] = useState<PartnerClient | null>(null);
  const [detachReason, setDetachReason] = useState("");
  const [stopSubscription, setStopSubscription] = useState(false);
  const [detaching, setDetaching] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);

  const rootBasePath = `/${customerId}/${entityId}`;
  const emailSendContext = useRootSendContext({
    rootBasePath,
    type: SEND_MESSAGE_TYPE.SINGLE,
    toSendMsgUserId: partnerId ?? "",
    rootEntityIdentifications: [],
    show: emailOpen,
    singleEmailPath: `${rootBasePath}/system/partner/${encodeURIComponent(
      partnerId ?? ""
    )}/email`,
  });

  const openSignedContract = async () => {
    if (!partnerId) return;
    try {
      const { url } = await PartnerAdminService.signedContract(
        customerId,
        entityId,
        partnerId
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const wanted = useRef<string | null>(null);

  const load = async (id: string) => {
    wanted.current = id;
    setLoading(true);
    try {
      const detail = await PartnerAdminService.get(customerId, entityId, id);
      if (wanted.current !== id) return;
      setPartner(detail);
    } catch (error) {
      if (wanted.current !== id) return;
      toast.error(errorMessage(error));
    } finally {
      if (wanted.current === id) setLoading(false);
    }
  };

  useEffect(() => {
    wanted.current = partnerId;
    setPartner(null);
    setAttachId("");
    setAttachReason("");
    setWarnings(null);
    setAttachOpen(false);
    setEmailOpen(false);
    setDetachTarget(null);
    setDetachReason("");
    setStopSubscription(false);
    if (partnerId) load(partnerId);
  }, [partnerId]);

  const refresh = async () => {
    if (partnerId) await load(partnerId);
    onChanged();
  };

  const attach = async (force: boolean) => {
    if (!partnerId || !attachId.trim()) return;
    setAttaching(true);
    try {
      const result = await PartnerAdminService.attach(
        customerId,
        entityId,
        partnerId,
        {
          entity_identification: attachId.trim(),
          reason: attachReason.trim() || undefined,
          force,
        }
      );
      if (!result.attached) {
        setWarnings(result.warnings);
        return;
      }
      toast.success("Account attached");
      setAttachOpen(false);
      setWarnings(null);
      setAttachId("");
      setAttachReason("");
      await refresh();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setAttaching(false);
    }
  };

  const detach = async () => {
    if (!partnerId || !detachTarget) return;
    setDetaching(true);
    try {
      const stop = Boolean(detachTarget.sponsored?.running) && stopSubscription;
      const result = await PartnerAdminService.detach(
        customerId,
        entityId,
        partnerId,
        {
          entity_identification: detachTarget.entity_id,
          reason: detachReason.trim(),
          ...(stop ? { stop_subscription: true } : {}),
        }
      );
      toast.success(
        stop && result.subscription_stopped
          ? "Account detached, subscription set to stop at period end"
          : stop
          ? "Account detached, but the subscription could not be stopped"
          : "Account detached"
      );
      setDetachTarget(null);
      setDetachReason("");
      setStopSubscription(false);
      await refresh();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setDetaching(false);
    }
  };

  const isDistributor = partner?.program === "distributor";
  const current =
    partner?.clients.filter((client) => !client.detached_at) ?? [];
  const detached =
    partner?.clients.filter((client) => client.detached_at) ?? [];

  const closeAttach = () => {
    setAttachOpen(false);
    setWarnings(null);
  };

  return (
    <SideDrawer
      open={Boolean(partnerId)}
      onClose={onClose}
      title={partner ? partnerName(partner) : "Partner"}
      icon={HiUserGroup}
      footer={
        <>
          {partner && (
            <div className="sticky bottom-0 left-0 right-0 -mx-4 -mb-4 border-t border-gray-200 bg-white px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                {isDistributor && (
                  <CustomButton
                    onClick={() => setAttachOpen(true)}
                    variant="primary"
                    size="sm"
                    title="Attach account"
                    icon="plus"
                  />
                )}
                {partner.email && (
                  <CustomButton
                    onClick={() => setEmailOpen(true)}
                    variant="outline"
                    size="sm"
                    title="Email partner"
                    icon="envelop"
                  />
                )}
                {partner.contract?.has_signed_pdf && (
                  <CustomButton
                    onClick={openSignedContract}
                    variant="outline"
                    size="sm"
                    title="Signed contract"
                    icon={<HiDocumentDownload size={18} />}
                  />
                )}
              </div>
            </div>
          )}

          <SendPersonalMessage
            show={emailOpen}
            ctrFn={(v) => {
              const next = typeof v === "function" ? v(true) : v;
              if (!next) setEmailOpen(false);
            }}
            type={SEND_MESSAGE_TYPE.SINGLE}
            sendContext={emailSendContext}
            onResolved={() => setEmailOpen(false)}
          />

          <ModalShell
            show={attachOpen}
            onClose={closeAttach}
            title={
              warnings
                ? "Attach despite the warnings?"
                : "Attach an existing account"
            }
            footer={
              <div className="flex flex-wrap items-center justify-end gap-2">
                <CustomButton
                  variant="secondary"
                  title="Cancel"
                  onClick={closeAttach}
                />
                {warnings ? (
                  <CustomButton
                    variant="warning"
                    title="Attach anyway"
                    disabled={!attachReason.trim() || attaching}
                    onClick={() => attach(true)}
                  />
                ) : (
                  <CustomButton
                    variant="primary"
                    title="Attach"
                    disabled={!attachId.trim() || attaching}
                    onClick={() => attach(false)}
                  />
                )}
              </div>
            }
          >
            <div className="flex flex-col gap-3">
              {warnings ? (
                <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
                  {warnings.map((warning, index) => (
                    <li key={`${warning.code}-${index}`}>
                      {warningText(warning)}
                    </li>
                  ))}
                </ul>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Only for a free account that never paid, at the written
                    request of the distributor. Neither the client nor the
                    distributor can do it themselves.
                  </p>
                  <input
                    className={FIELD}
                    placeholder="Account (entity) identifier"
                    value={attachId}
                    onChange={(e) => setAttachId(e.target.value)}
                  />
                </>
              )}
              <textarea
                className={FIELD}
                rows={3}
                placeholder={
                  warnings
                    ? "Reason, kept with the attachment (required)"
                    : "Reason, for example the date of the written request"
                }
                value={attachReason}
                onChange={(e) => setAttachReason(e.target.value)}
              />
            </div>
          </ModalShell>

          <ModalShell
            show={Boolean(detachTarget)}
            onClose={() => {
              setDetachTarget(null);
              setStopSubscription(false);
            }}
            title="Detach this account?"
            subtitle={detachTarget?.entity_name || detachTarget?.entity_id}
            footer={
              <div className="flex flex-wrap items-center justify-end gap-2">
                <CustomButton
                  variant="secondary"
                  title="Cancel"
                  onClick={() => {
                    setDetachTarget(null);
                    setStopSubscription(false);
                  }}
                />
                <CustomButton
                  variant="danger"
                  title="Detach"
                  disabled={detachReason.trim().length < 3 || detaching}
                  onClick={detach}
                />
              </div>
            }
          >
            <p className="mb-3 text-sm text-gray-700">
              The account leaves the partner&apos;s list and its active clients.
              The attachment is kept with the date, your name and the reason.
            </p>
            <textarea
              className={FIELD}
              rows={3}
              placeholder="Reason (required)"
              value={detachReason}
              onChange={(e) => setDetachReason(e.target.value)}
            />
            {detachTarget?.sponsored?.running && (
              <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={stopSubscription}
                  onChange={(e) => setStopSubscription(e.target.checked)}
                />
                <span>
                  Also stop the {tierLabel(detachTarget.sponsored.plan_tier)}{" "}
                  subscription this partner pays for the account. It keeps its
                  plan until the period ends, then it is not renewed.
                </span>
              </label>
            )}
          </ModalShell>
        </>
      }
    >
      {loading && !partner ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="xl" />
        </div>
      ) : !partner ? (
        <p className="py-16 text-center text-sm text-gray-500">
          No partner selected.
        </p>
      ) : (
        <div className="flex h-full flex-col gap-5 pb-24">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-3">
              <UserAvatar
                email={partner.email}
                name={partnerName(partner)}
                sizeClass="w-10 h-10"
              />
              <div className="min-w-0">
                <p className="truncate font-semibold text-gray-900">
                  {partnerName(partner)}
                </p>
                <div className="text-xs text-gray-500">
                  <Copy target={partner.partner_id} truncate longVersion />
                </div>
              </div>
              <span className="ml-auto flex flex-wrap items-center justify-end gap-2">
                {programBadge(partner.program)}
                {partner.contract && contractBadge(partner.contract.status)}
              </span>
            </div>

            <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
              <dt className="text-gray-500">Email</dt>
              <dd className="text-gray-700">
                {partner.email ? (
                  <NoTranslate>{partner.email}</NoTranslate>
                ) : (
                  "None"
                )}
              </dd>
              <dt className="text-gray-500">Company</dt>
              <dd className="text-gray-700">
                {partner.company_name || "None"}
              </dd>
              <dt className="text-gray-500">Business number</dt>
              <dd className="text-gray-700">
                {partner.business_number || "None"}
              </dd>
              <dt className="text-gray-500">Contract signed</dt>
              <dd className="tabular-nums text-gray-700">
                {formatDay(partner.contract_signed_at)}
              </dd>
              <dt className="text-gray-500">Active clients</dt>
              <dd className="tabular-nums text-gray-700">
                {partner.active_clients} of {partner.attached_clients}
              </dd>
              {isDistributor && (
                <>
                  <dt className="text-gray-500">Milestone</dt>
                  <dd className="text-gray-700">
                    {partner.milestone
                      ? `${partner.milestone} active clients reached`
                      : "Below 10 active clients"}
                  </dd>
                </>
              )}
            </dl>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Identity and address
            </p>
            <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
              <dt className="text-gray-500">Birth date</dt>
              <dd className="tabular-nums text-gray-700">
                {formatDay(partner.identity.birth_date)}
              </dd>
              <dt className="text-gray-500">Phone</dt>
              <dd className="text-gray-700">
                {partner.identity.phone || "None"}
              </dd>
              <dt className="text-gray-500">Country</dt>
              <dd className="text-gray-700">
                <NoTranslate>{partner.identity.country || "None"}</NoTranslate>
              </dd>
              <dt className="self-start text-gray-500">Address</dt>
              <dd className="text-gray-700">
                {[
                  partner.address.line1,
                  partner.address.line2,
                  [partner.address.zipcode, partner.address.city]
                    .filter(Boolean)
                    .join(" "),
                  partner.address.state,
                ]
                  .filter(Boolean)
                  .join(", ") || "None"}
              </dd>
              {!isDistributor && (
                <>
                  <dt className="text-gray-500">Affiliate level</dt>
                  <dd className="text-gray-700">
                    {levelLabel(partner.follow_up.partner_level)}
                  </dd>
                </>
              )}
              <dt className="text-gray-500">Validated</dt>
              <dd className="text-gray-700">
                {partner.follow_up.is_validated ? "Yes" : "No"}
              </dd>
            </dl>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Payout
            </p>
            <dl className="grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
              <dt className="text-gray-500">Method</dt>
              <dd className="text-gray-700">
                {payoutMethodLabel(partner.payout.method)}
              </dd>
              {partner.payout.bank && (
                <>
                  <dt className="text-gray-500">Account holder</dt>
                  <dd className="text-gray-700">
                    {partner.payout.bank.holder || "None"}
                  </dd>
                  <dt className="text-gray-500">Bank</dt>
                  <dd className="text-gray-700">
                    {partner.payout.bank.bank_name || "None"}
                  </dd>
                  <dt className="text-gray-500">IBAN</dt>
                  <dd className="tabular-nums text-gray-700">
                    <NoTranslate>{partner.payout.bank.iban}</NoTranslate>
                  </dd>
                  <dt className="text-gray-500">BIC</dt>
                  <dd className="text-gray-700">
                    <NoTranslate>
                      {partner.payout.bank.bic || "None"}
                    </NoTranslate>
                  </dd>
                </>
              )}
              {partner.payout.paypal && (
                <>
                  <dt className="text-gray-500">PayPal</dt>
                  <dd className="text-gray-700">
                    <NoTranslate>{partner.payout.paypal.address}</NoTranslate>
                  </dd>
                </>
              )}
            </dl>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Clients <span className="tabular-nums">({current.length})</span>
            </p>
            {current.length === 0 ? (
              <p className="text-sm text-gray-500">No attached client.</p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                {current.map((client) => (
                  <li
                    key={client.entity_id}
                    className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center"
                  >
                    <div className="min-w-0 flex-1 sm:basis-64">
                      <Link
                        to={`/control-panel/monitoring?entityId=${encodeURIComponent(
                          client.entity_id
                        )}`}
                        className="block truncate text-sm font-medium text-gray-900 hover:text-blue-700"
                      >
                        <NoTranslate>{client.entity_id}</NoTranslate>
                      </Link>
                      {client.entity_name && (
                        <p className="truncate text-xs text-gray-700">
                          {client.entity_name}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        {tierLabel(client.plan_tier)}
                        {client.renewal_date
                          ? `, renews ${formatPayModDay(client.renewal_date)}`
                          : ""}
                        {` · attached ${formatDay(
                          client.registration_date_time
                        )}`}
                        {client.attached_by
                          ? client.attached_by.forced
                            ? " by root, despite warnings"
                            : " by root"
                          : ""}
                        {client.attached_by?.reason
                          ? `: ${client.attached_by.reason}`
                          : ""}
                      </p>
                      {client.sponsored && (
                        <p className="text-xs text-gray-700">
                          {sponsoredText(client.sponsored)}
                        </p>
                      )}
                      {client.legal_identifier && (
                        <p className="text-xs text-gray-500">
                          Legal identifier{" "}
                          <NoTranslate>{client.legal_identifier}</NoTranslate>
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {subscriptionBadge(client.status)}
                      <CustomButton
                        variant="secondary"
                        size="xs"
                        title="Detach"
                        onClick={() => {
                          setDetachTarget(client);
                          setDetachReason("");
                          setStopSubscription(false);
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {(partner.program === "distributor" ||
            partner.program === "affiliate") && (
            <PartnerLedgerSection
              key={partner.partner_id}
              customerId={customerId}
              entityId={entityId}
              partnerId={partner.partner_id}
              isDistributor={isDistributor}
              clients={partner.clients}
            />
          )}

          {detached.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Detached clients{" "}
                <span className="tabular-nums">({detached.length})</span>
              </p>
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                {detached.map((client) => (
                  <li
                    key={`${client.entity_id}-${client.detached_at}`}
                    className="px-4 py-3"
                  >
                    <p className="truncate text-sm font-medium text-gray-900">
                      <NoTranslate>{client.entity_id}</NoTranslate>
                    </p>
                    {client.entity_name && (
                      <p className="truncate text-xs text-gray-700">
                        {client.entity_name}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      Detached {formatDay(client.detached_at)}:{" "}
                      {client.detached_reason}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </SideDrawer>
  );
};

export default PartnerDrawer;
